import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Briefcase, Coffee, PartyPopper, Plane, Gift } from 'lucide-react';
import { TRYON_PAGE_CONFIG, MOMENTS_PAGE_CONFIG } from '../config/marketingConfig';

// =============================================================================
// CONFIGURATION
// =============================================================================
const TRYON_CONFIG = TRYON_PAGE_CONFIG;
const MOMENTS_CONFIG = MOMENTS_PAGE_CONFIG;
const SIZE_RECOMMENDATION_IMAGE = "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260215085750.webp";

const momentIcons = {
  creative_workday: Briefcase,
  unwind: Coffee,
  after_hours: PartyPopper,
  the_escape: Plane,
  gentle_celebration: Gift
};

// =============================================================================
// VIRTUAL TRY-ON ANIMATION
// =============================================================================
const VirtualTryOnAnimation = ({ isInView }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, [isInView]);

  return (
    <div className="relative w-full aspect-[4/3] lg:aspect-[16/10] rounded-2xl overflow-hidden bg-slate-50">
      <div className="absolute inset-0 flex items-center justify-center gap-3 sm:gap-6 p-6 sm:p-10">
        {/* Human */}
        <motion.div 
          className="relative w-[28%] max-w-[180px] aspect-[3/4] rounded-xl overflow-hidden shadow-lg"
          animate={{ 
            opacity: step >= 0 ? 1 : 0.4,
            scale: step === 0 ? 1.02 : 1
          }}
          transition={{ duration: 0.4 }}
        >
          <img
            src={TRYON_CONFIG.demo.humanImage}
            alt="Customer"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {step === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 ring-2 ring-blue-500 ring-offset-2 rounded-xl"
            />
          )}
        </motion.div>

        {/* Plus */}
        <motion.span 
          className="text-slate-300 text-2xl sm:text-3xl font-light"
          animate={{ opacity: step >= 1 ? 0.8 : 0.3 }}
        >
          +
        </motion.span>

        {/* Dress */}
        <motion.div 
          className="relative w-[28%] max-w-[180px] aspect-[3/4] rounded-xl overflow-hidden shadow-lg"
          animate={{ 
            opacity: step >= 1 ? 1 : 0.4,
            scale: step === 1 ? 1.02 : 1
          }}
          transition={{ duration: 0.4 }}
        >
          <img
            src={TRYON_CONFIG.demo.dressImage}
            alt="Dress"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 ring-2 ring-blue-500 ring-offset-2 rounded-xl"
            />
          )}
        </motion.div>

        {/* Arrow */}
        <motion.div
          animate={{ 
            opacity: step >= 2 ? 1 : 0.3,
            x: step === 2 ? [0, 4, 0] : 0
          }}
          transition={{ duration: 0.5, repeat: step === 2 ? Infinity : 0 }}
          className="text-blue-500"
        >
          <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </motion.div>

        {/* Output */}
        <motion.div 
          className="relative w-[28%] max-w-[180px] aspect-[3/4] rounded-xl overflow-hidden shadow-xl"
          animate={{ 
            opacity: step >= 3 ? 1 : 0.4,
            scale: step === 3 ? 1.02 : 1
          }}
          transition={{ duration: 0.4 }}
        >
          <img
            src={TRYON_CONFIG.demo.outputImage}
            alt="Try-On Result"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 ring-2 ring-emerald-500 ring-offset-2 rounded-xl"
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

// =============================================================================
// MOMENTS ANIMATION
// =============================================================================
const MomentsAnimation = ({ isInView }) => {
  const [selectedMoment, setSelectedMoment] = useState('creative_workday');
  const moments = Object.keys(MOMENTS_CONFIG.moments);

  // Tab labels mapping
  const tabLabels = {
    creative_workday: 'Workday',
    unwind: 'Unwind',
    after_hours: 'After Hours',
    the_escape: 'Escape',
    gentle_celebration: 'Celebration'
  };

  useEffect(() => {
    if (!isInView) return;
    const interval = setInterval(() => {
      setSelectedMoment(prev => {
        const idx = moments.indexOf(prev);
        return moments[(idx + 1) % moments.length];
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [isInView, moments]);

  const currentMoment = MOMENTS_CONFIG.moments[selectedMoment];
  const Icon = momentIcons[selectedMoment];

  return (
    <div className="relative w-full">
      {/* Main Image */}
      <div className="relative aspect-[3/4] lg:aspect-[4/5] rounded-2xl overflow-hidden bg-slate-900">
        <AnimatePresence mode="wait">
          <motion.img
            key={selectedMoment}
            src={currentMoment.image}
            alt={currentMoment.label}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </AnimatePresence>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Label */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-white/80" />}
          <span className="text-white font-medium text-sm">{currentMoment.label}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex justify-center gap-2 mt-4 flex-wrap">
        {moments.map((key) => {
          const MomentIcon = momentIcons[key];
          const isSelected = key === selectedMoment;
          return (
            <button
              key={key}
              onClick={() => setSelectedMoment(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                isSelected
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              <MomentIcon className="w-3 h-3" />
              <span>{tabLabels[key]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// SIZE RECOMMENDATION VISUAL
// =============================================================================
const SizeRecommendationVisual = () => {
  return (
    <div className="relative w-full aspect-[3/4] lg:aspect-[4/5] rounded-2xl overflow-hidden bg-slate-50">
      <img
        src={SIZE_RECOMMENDATION_IMAGE}
        alt="Smart Size Recommendation"
        className="w-full h-full object-cover object-top"
        loading="lazy"
      />
    </div>
  );
};

// =============================================================================
// FEATURE ROW COMPONENT
// =============================================================================
const FeatureRow = ({ label, headline, description, subdescription, visual, reversed }) => {
  return (
    <div className={`flex flex-col ${reversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-10 lg:gap-16`}>
      {/* Text */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
        className={`flex-1 text-center ${reversed ? 'lg:text-left' : 'lg:text-left'}`}
      >
        <span className="inline-block text-[11px] font-semibold text-blue-600 uppercase tracking-[0.2em] mb-3">
          {label}
        </span>
        <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
          {headline}
        </h3>
        <p className="text-lg text-slate-500 leading-relaxed">
          {description}
        </p>
        {subdescription && (
          <p className="text-sm text-slate-400 mt-2">
            {subdescription}
          </p>
        )}
      </motion.div>

      {/* Visual */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="flex-1 w-full"
      >
        {visual}
      </motion.div>
    </div>
  );
};

// =============================================================================
// MAIN SECTION
// =============================================================================
export const TryOnStudioSection = () => {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="tryon-studio" 
      className="py-24 sm:py-32 bg-white"
      data-testid="tryon-studio-section"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 sm:mb-28"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-3">
            TryOn Studio<sup className="text-lg align-super">™</sup>
          </h2>
          <div className="w-12 h-0.5 bg-blue-600 mx-auto mb-6" />
          <p className="text-xl text-slate-500 tracking-wide">
            A unified platform to deliver interactive, try-before-you-buy shopping experiences.
          </p>
          <p className="mt-2 text-base text-slate-500">
            Go live in minutes - via Shopify, API, or simple shareable links.
          </p>
          <p className="mt-3 text-base text-slate-400">
            Three core modules built to drive shopper confidence and sales:
          </p>
        </motion.div>

        {/* Feature Rows */}
        <div className="space-y-24 sm:space-y-32">
          
          {/* Row 1: Smart Size */}
          <FeatureRow
            label="Smart Size"
            headline="Right size. First time."
            description="AI-powered size recommendations with high confidence."
            subdescription="Fewer returns."
            visual={<SizeRecommendationVisual />}
            reversed={false}
          />

          {/* Row 2: Virtual Try-On */}
          <FeatureRow
            label="Virtual Try-On"
            headline="See it on you."
            description="Upload a photo. Pick a dress. Instantly preview the fit."
            visual={<VirtualTryOnAnimation isInView={isInView} />}
            reversed={true}
          />

          {/* Row 3: Moments */}
          <FeatureRow
            label="Moments"
            headline="See the moment."
            description="Experience the same outfit across real-life settings."
            subdescription="Before you decide."
            visual={<MomentsAnimation isInView={isInView} />}
            reversed={false}
          />

        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mt-24 sm:mt-32"
        >
          <Link
            to="/tryon-studio"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full font-semibold text-lg shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 hover:scale-[1.02] transition-all duration-300"
            data-testid="tryon-studio-cta"
          >
            Know more about TryOn Studio
          </Link>
          <p className="mt-4 text-slate-400 text-sm font-medium tracking-wide">
            No hardware. No complex setup. No hidden costs.
          </p>
        </motion.div>

      </div>
    </section>
  );
};

export default TryOnStudioSection;
